import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormatters } from '../../../hooks';
import { reverseDriverPayment } from '../../../redux/Users/actions';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';

// Confirmation dialog for reversing one specific admin-issued payment. The
// payment is identified by its audit-log row id, so the backend reverses only
// that transfer's remaining un-reversed amount.
function DriverPaymentReverseDialog({ isOpen, onClose, onReversed, user, entry }) {
  const f = useFormatters();
  const dispatch = useDispatch();
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setNote('');
      setSubmitError(null);
    }
  }, [isOpen]);

  const reversibleDollars = entry ? (entry.reversibleCents ?? 0) / 100 : 0;

  function handleClose() {
    if (isSubmitting) {
      return;
    }
    onClose();
  }

  async function handleConfirm() {
    if (!entry) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await reverseDriverPayment({
        userId: user.id,
        auditLogId: entry.id,
        description: note.trim() || undefined,
      })(dispatch);

      if (result.success) {
        if (onReversed) {
          await onReversed();
        }
        onClose();
      } else {
        const backendMessage = result.error?.response?.data?.message || result.error?.message;
        setSubmitError(backendMessage || 'Could not reverse this payment. Please try again later.');
      }
    } catch (error) {
      setSubmitError('Could not reverse this payment. Please try again later.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Reverse Payment</DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Stack spacing="20px" sx={{ paddingTop: '8px' }}>
          <Typography variant="body2" color="text.secondary">
            This reverses the remaining {f.number.currency(reversibleDollars)} of this payment back off{' '}
            {user.firstName || 'this driver'}&rsquo;s Stripe balance. This moves real funds and cannot be undone.
          </Typography>

          {entry && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', padding: '16px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Payment issued
                </Typography>
                <Typography variant="body2">{f.number.currency((entry.amountCents ?? 0) / 100)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt="8px">
                <Typography variant="subtitle2">Reversing now</Typography>
                <Typography variant="subtitle1" color="error.main">
                  {f.number.currency(reversibleDollars)}
                </Typography>
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note (optional)"
            placeholder="Why is this payment being reversed?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: '0 32px 32px 32px' }}>
        {submitError ? (
          <Alert severity="error" sx={{ width: '100%' }}>
            {submitError}
          </Alert>
        ) : (
          <Button variant="contained" fullWidth color="error" disabled={isSubmitting} onClick={handleConfirm}>
            {isSubmitting ? 'Reversing...' : `Reverse ${f.number.currency(reversibleDollars)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default DriverPaymentReverseDialog;
